import { t } from 'i18next';
import * as yup from 'yup';

import { WHATSAPP_FLOW_LIMITS } from '../constants';
import { contextVariableWithBracesGlobal } from '../patterns';

// Counts the effective length of a button label as WhatsApp would see it.
// Context variables like `{{$user.first_name}}` resolve at runtime, so strip
// them from the source before counting (matching the `flow_cta` pattern below).
export const getWhatsappButtonLabelLength = (value: string) =>
  value.replace(contextVariableWithBracesGlobal, '').length;

export const whatsappFlowSchema = yup.object().shape({
  flow_id: yup.string().required(() => t('validation.required')),
  flow_body: yup
    .string()
    .required(() => t('validation.required'))
    .max(WHATSAPP_FLOW_LIMITS.BODY, () =>
      t('validation.less_than', { 0: WHATSAPP_FLOW_LIMITS.BODY })
    ),
  flow_cta: yup
    .string()
    .required(() => t('validation.required'))
    .test('cta-max-length', '', function (value) {
      if (!value) {
        return true;
      }
      const textWithoutVariables = value.replace(contextVariableWithBracesGlobal, '');
      if (textWithoutVariables.length > WHATSAPP_FLOW_LIMITS.CTA) {
        return this.createError({
          message: t('validation.less_than', { 0: WHATSAPP_FLOW_LIMITS.CTA }),
        });
      }
      return true;
    }),
  flow_header: yup
    .string()
    .max(WHATSAPP_FLOW_LIMITS.HEADER, () =>
      t('validation.less_than', { 0: WHATSAPP_FLOW_LIMITS.HEADER })
    ),
  flow_footer: yup
    .string()
    .max(WHATSAPP_FLOW_LIMITS.FOOTER, () =>
      t('validation.less_than', { 0: WHATSAPP_FLOW_LIMITS.FOOTER })
    ),
  flow_action: yup
    .string()
    .oneOf(['navigate', 'data_exchange'])
    .required(() => t('validation.required')),
  screen: yup
    .string()
    .when('flow_action', {
      is: 'navigate',
      then: (schema) =>
        schema
          .required(() => t('validation.required'))
          .max(WHATSAPP_FLOW_LIMITS.SCREEN, () =>
            t('validation.less_than', { 0: WHATSAPP_FLOW_LIMITS.SCREEN })
          ),
      otherwise: (schema) => schema.notRequired(),
    }),
  initial_data: yup.string().test(
    'valid-json-object',
    () => t('dialog.flow.invalid_json_object'),
    (value) => {
      if (!value || value.trim() === '') {
        return true;
      }
      try {
        // This only removes backslashes that exist just to escape newlines, without touching valid JSON escapes like
        // `\\` inside strings.
        const cleaned = value.replace(/\\\s*\n/g, '\n');
        const parsed = JSON.parse(cleaned);
        return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed);
      } catch {
        return false;
      }
    }
  ),
  trigger_node_id: yup.string(),
});
